import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import {
  MdVisibility,
  MdDescription,
  MdPerson,
  MdBusiness,
  MdInfo,
  MdLocalAirport,
  MdPhone,
  MdEmail,
  MdCalendarToday,
  MdSettings
} from "react-icons/md";
import { createApi } from "../../../../core/api";

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "28px 160px 1fr",
      gap: 10,
      padding: "10px 0",
      borderBottom: "1px solid #F1F5F9",
      fontSize: 13,
    }}
  >
    <Icon size={18} color="#64748B" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 600, color: "#1E293B" }}>{value ?? "-"}</span>
  </div>
);

const Card = ({ icon: Icon, title, children }: any) => (
  <div
    style={{
      border: "1px solid #E2E8F0",
      borderRadius: 16,
      padding: 20,
      background: "white",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 16,
        paddingBottom: 10,
        borderBottom: "2px solid #F8FAFC",
        color: "#1E293B",
      }}
    >
      <Icon size={20} color="#3B82F6" />
      <strong style={{ fontSize: 15, textTransform: "uppercase", letterSpacing: "0.5px" }}>{title}</strong>
    </div>
    {children}
  </div>
);

const LocalSupportDetailModal = ({ data, onClose }: any) => {
  const airportCode = data.airport?.codeIATA || data.airport?.icao || data.airport?.iata || "-";
  const airportName = data.airport ? `${data.airport.name} (${airportCode})` : "-";
  const fullName = data.user ? `${data.user.firstName} ${data.user.lastName}` : (data.user?.displayName || "-");

  return (
    <Modal open onClose={onClose}>
      <div style={{ padding: "32px", minWidth: 600, maxWidth: 800 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
           <h2 style={{ fontSize: 22, fontWeight: 900, color: "#0F172A" }}>Local Support Request Detail</h2>
           <Badge label={`ID: ${data.id}`} color="#3B82F6" />
        </div>
        
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <Card icon={MdLocalAirport} title="Airport Information">
               <InfoRow icon={MdLocalAirport} label="Airport" value={airportName} />
               <InfoRow icon={MdSettings} label="Service Type" value={`Type ${data.serviceType}`} />
            </Card>

            <Card icon={MdDescription} title="Timing & Reference">
               <InfoRow icon={MdCalendarToday} label="Created Date" value={new Date(data.createdDate).toLocaleString()} />
               <InfoRow icon={MdInfo} label="Status" value="Submitted" />
            </Card>
          </div>

          <Card icon={MdDescription} title="Request Description">
            <div style={{ whiteSpace: "pre-wrap", padding: "10px 0", color: "#334155", fontSize: 14, lineHeight: "1.6", minHeight: 60 }}>
              {data.description || "No description provided."}
            </div>
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <Card icon={MdPerson} title="User Contact Details">
               <InfoRow icon={MdPerson} label="Full Name" value={fullName} />
               <InfoRow icon={MdEmail} label="Email Address" value={data.user?.email} />
               <InfoRow icon={MdPhone} label="Mobile Phone" value={data.user?.phoneNumber} />
               <InfoRow icon={MdPhone} label="Office Phone" value={data.user?.officePhoneNumber} />
            </Card>

            <Card icon={MdBusiness} title="Company Information">
               <InfoRow icon={MdBusiness} label="Company Name" value={data.company?.companyName || data.company?.name || "-"} />
               <InfoRow icon={MdInfo} label="User Role" value={data.user?.role?.name || "Member"} />
            </Card>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const LocalSupportList = () => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailRow, setDetailRow] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r: any = await (api as any).dashboard.getLocalSupportsAll();
    setData(r?.data?.data ?? r?.data ?? []);
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: "id",
        title: "ID",
        accessor: (r: any) => r.id,
        minWidth: 70,
      },
      {
        key: "airport",
        title: "Airport",
        accessor: (r: any) => r.airport?.name ?? "-",
        render: (r: any) => (
          <div style={{ fontWeight: 600, color: "#1E293B" }}>
            {r.airport?.codeIATA || r.airport?.icao || r.airport?.iata || "-"}
            <div style={{ fontSize: 11, fontWeight: 500, color: "#64748B" }}>{r.airport?.name}</div>
          </div>
        )
      },
      {
        key: "description",
        title: "Description",
        accessor: (r: any) => r.description,
        render: (_: any, v: string) => (
          <div style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>
            {v}
          </div>
        ),
      },
      {
        key: "user",
        title: "Requested By",
        accessor: (r: any) => `${r.user?.firstName} ${r.user?.lastName}`,
        render: (r: any) => (
          <div>
            <div style={{ fontWeight: 600 }}>{r.user?.firstName} {r.user?.lastName}</div>
            <div style={{ fontSize: 11, color: "#64748B" }}>{r.company?.companyName || r.company?.name}</div>
          </div>
        )
      },
      {
        key: "date",
        title: "Date",
        accessor: (r: any) => new Date(r.createdDate).toLocaleDateString(),
      },
      {
        key: "actions",
        title: "Actions",
        render: (r: any) => (
          <GroupButton
            items={[
              {
                key: "detail",
                icon: MdVisibility,
                color: "#2563EB",
                onClick: () => setDetailRow(r),
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <div style={{ padding: 32, background: "#F8FAFC", minHeight: "100vh" }}>
      <div style={{ marginBottom: 30, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.5px" }}>Local Support Requests</h1>
          <p style={{ fontSize: 14, color: "#64748B", marginTop: 4 }}>Detailed monitoring of ground service and local support submissions</p>
        </div>
      </div>

      <div style={{ background: "white", borderRadius: 20, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)", overflow: "hidden", border: "1px solid #E2E8F0" }}>
        <Table loading={loading} columns={columns} data={data} />
      </div>

      {detailRow && (
        <LocalSupportDetailModal
          data={detailRow}
          onClose={() => setDetailRow(null)}
        />
      )}
    </div>
  );
};
